/**
 * Title offered to this account, as the buyer reads it: each offer with its
 * price, its seller, who brokered it, and what a covenant would oblige them to.
 *
 * The seller's side — making, withdrawing or unlocking an offer — is the
 * gallery's and the work record's, not this screen's. The two reads and the
 * answer are `useTransferOffersController.ts`'s; the words are
 * `transfers.constants.ts`'s.
 *
 * The covenant is a shape and not a sentence, because a resale LOCK is a
 * different kind of fact from a share of profit and must be renderable as a
 * warning on its own. An expired term reads as no covenant at all —
 * `covenantState`'s derivation, the same one Sold Works uses.
 */

import type { CollectionSummary, TransferOffer } from "@ondo/shared";
import { formatMoney } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatDay } from "../lib/dates.ts";
import { OFFERS_COPY } from "./transfers.constants.ts";

/** One offer, with every label already decided. */
export interface OfferRow {
  transferId: string;
  workId: string;
  ondoCode: string;
  title: string;
  /** 'John Currin · 2007'. Absent parts dropped. */
  identity: string;
  /** '£1,450,000'. Never empty: a sale has a price or it did not happen. */
  price: string;
  /** 'Sadie Coles HQ · ONDO-1024', the principal whose title is moving. */
  seller: string;
  /** The seller's id on its own, for "Check who this is" (OND-81). */
  sellerOndoId: string;
  /** Who brokered it, or the sentence saying nobody did. */
  through: string;
  covenant: CovenantNotice;
  /**
   * What an EARLIER sale left on the work and will bind this buyer's own onward
   * sale. OND-85, and a different fact from `covenant` above.
   */
  inherited: InheritedNotice;
  /**
   * WHICH HAT THIS ONE IS ACCEPTED IN — OND-184, and null is the common case.
   *
   * `ondo.my_transfer_offers()` is bounded by the VIEWER rung on the receiving
   * side, so a dealer at a gallery reads the firm's purchases beside their own
   * and nothing used to say which was which. The work then landed in whichever
   * collection got picked, and `ondo.work_owner_principal_id()` answered the
   * dealer about a picture the gallery had bought.
   *
   * Null when the buyer IS the reader, which is every offer a collector with no
   * firm will ever see — the ticket's "a collector with no firm sees no question
   * and nothing changes for them". The only capacity worth a sentence is the one
   * that is not about you.
   */
  capacity: CapacityNotice | null;
  /**
   * The shelves `ondo.answer_transfer()` will accept for THIS offer: the ones
   * owned by the party title moves to.
   *
   * PER ROW AND NOT PER SCREEN, which is the whole shape change. One list for
   * the screen was the client half of the fault: the door asked only whether
   * the CALLER owned the collection, so every collection the reader owned was
   * offered for every purchase, including the firm's.
   */
  collections: CollectionSummary[];
  /** Null when there is at least one. Otherwise what to do about it. */
  noCollections: string | null;
  /**
   * What to send as `asOndoId` — the party the sale names, always, whether that
   * is the reader or a firm.
   *
   * The same value either way because `ondo.answer_transfer()` resolves it
   * within `ondo.principal_ids_at_least('admin')`, which always contains the
   * caller's own person principal. So the screen states the capacity it is
   * acting in on every acceptance and never has to decide whether to.
   */
  asOndoId: string;
  /** The work's own record — readable while the offer is open, and not after. */
  href: string;
  imageUrl: string | null;
}

/**
 * The party a purchase is being made for, when it is not the reader.
 *
 * A shape rather than a sentence for `CovenantNotice`'s reason: the id has to be
 * renderable on its own, because what a director checks when something looks
 * wrong is the id and not the name.
 */
export interface CapacityNotice {
  /** 'This purchase is the gallery's, not yours.' */
  lead: string;
  /** 'Lindqvist & Achebe · ONDO-5533' */
  party: string;
  ondoId: string;
}

/**
 * What a covenant obliges the buyer to, or that there is none.
 *
 * A shape rather than a string, because the LOCK has to be renderable as a
 * warning in its own right. A term that merely shares resale profit is a cost;
 * one that forbids resale is a different kind of fact about the object, and
 * flattening both into one sentence would let the second hide inside the first.
 */
export type CovenantNotice =
  | { kind: "none"; summary: string }
  | { kind: "open"; summary: string; lock: string }
  | { kind: "locked"; summary: string; lock: string };

/**
 * What the buyer would INHERIT, and whether accepting is gated on reading it.
 *
 * `kind: "none"` is the only shape with no `mustAcknowledge`, and that absence
 * is the whole design: every other shape carries one, so a view cannot render
 * the obligation and forget the control that the server will insist on. The
 * screen cannot offer an accept button the database would refuse — which is the
 * same rule `yoursToAnswer` follows on the offers panel.
 */
export type InheritedNotice =
  | { kind: "none"; summary: string }
  | {
      kind: "owing" | "locked";
      /** 'This work carries a resale covenant from an earlier sale.' */
      summary: string;
      /** What it will cost on an onward sale, and by when. */
      cost: string;
      /** Whether it can stop a resale at all — the headline when it can. */
      lock: string;
      /**
       * That the profit is measured from the EARLIER sale's price and not from
       * what this buyer is about to pay — the thing most likely to be assumed
       * wrongly, and said without a figure because this row carries none.
       */
      basis: string;
      /**
       * Where the per-covenant terms are. The summary here is deliberately
       * figure-free: a list row showing two covenants' prices would put a third
       * party's sale figures into a view rendered in bulk, so the terms live on
       * `GET /v1/works/:id/inherited-covenants` and this is the way in.
       */
      detail: string;
      detailHref: string;
      /** True always on this arm. The server refuses an acceptance without it. */
      mustAcknowledge: true;
    };

export type OffersView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | {
      kind: "ready";
      rows: OfferRow[];
      summary: string;
    };

/**
 * Everything the screen renders, decided here.
 *
 * A discriminated union rather than a bag of flags, so "loading and also
 * errored" cannot be represented — the shape every screen in this app uses.
 */
export function toOffersView(input: {
  offers: TransferOffer[] | null;
  collections: CollectionSummary[] | null;
  error: string | null;
  images: ReadonlyMap<string, string>;
  workBase: string;
  /**
   * The reader's OWN ONDO id — OND-184, and the only thing this model needs to
   * tell a personal purchase from the firm's.
   *
   * It comes from `MeResponse.principal`, which the workspace already holds, and
   * not from the memberships list: the question is "is this buyer me", which one
   * id answers. Comparing against the seats would answer "is this buyer a firm I
   * work at", which is the same answer by a longer route and wrong the day a
   * work is offered to a firm the reader has since left.
   */
  selfOndoId: string;
  now?: Date;
}): OffersView {
  const { offers, collections, error, images, workBase, selfOndoId } = input;
  const now = input.now ?? new Date();

  if (error) return { kind: "error", title: OFFERS_COPY.errorTitle, message: error };
  // BOTH reads, not just the offers. The collection picker is not decoration —
  // an acceptance cannot be sent without one, so a screen rendered before they
  // arrive would offer a control that refuses.
  if (!offers || !collections) {
    return { kind: "loading", message: OFFERS_COPY.loading };
  }

  if (offers.length === 0) {
    return {
      kind: "empty",
      message: OFFERS_COPY.empty,
      detail: OFFERS_COPY.emptyDetail,
    };
  }

  return {
    kind: "ready",
    rows: offers.map((offer) => toRow(offer, workBase, images, now, collections, selfOndoId)),
    summary: OFFERS_COPY.count(offers.length),
  };
}

function toRow(
  offer: TransferOffer,
  workBase: string,
  images: ReadonlyMap<string, string>,
  now: Date,
  collections: CollectionSummary[],
  selfOndoId: string,
): OfferRow {
  // OND-184. THE SHELVES THAT BELONG TO THE BUYER, which is the predicate
  // `ondo.answer_transfer()` now enforces — so the control offers exactly what
  // the door will take. This is a MIRROR of the rule and not the rule: the
  // database refuses the rest whether or not this filter is here, which is why
  // the repair was made there first.
  //
  // `level === 'owner'` as well as the owner id, because the level is the
  // EFFECTIVE one and `ondo.owned_collection_ids()` asks for the admin rung: a
  // viewer-rung seat reads the firm's collection at 'viewer' and may not accept
  // into it.
  const shelves = collections.filter(
    (each) => each.ownerOndoId === offer.buyerOndoId && each.level === "owner",
  );
  const forFirm = offer.buyerOndoId !== selfOndoId;

  return {
    transferId: offer.transferId,
    workId: offer.workId,
    ondoCode: offer.ondoCode,
    title: offer.title,
    identity: describeIdentity(offer),
    price: formatMoney({ minor: offer.priceMinor, currency: offer.currency }),
    seller: `${offer.sellerName} · ${offer.sellerOndoId}`,
    sellerOndoId: offer.sellerOndoId,
    through: describeGallery(offer),
    covenant: describeCovenant(offer, now),
    inherited: describeInherited(offer, workBase, now),
    capacity: forFirm
      ? {
          lead: OFFERS_COPY.capacityLead,
          party: `${offer.buyerName} · ${offer.buyerOndoId}`,
          ondoId: offer.buyerOndoId,
        }
      : null,
    collections: shelves,
    noCollections:
      shelves.length > 0
        ? null
        : forFirm
          ? OFFERS_COPY.noFirmCollections(offer.buyerName)
          : OFFERS_COPY.noCollections,
    asOndoId: offer.buyerOndoId,
    href: `${workBase}/${offer.workId}`,
    imageUrl: images.get(offer.workId) ?? null,
  };
}

/** 'John Currin · 2007'. */
export function describeIdentity(offer: TransferOffer): string {
  return [offer.attributedName, offer.year]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/**
 * Who brokered the sale, named plainly when nobody did.
 *
 * `galleryOndoId` is null for a sale nobody brokered, and that is a real
 * arrangement rather than missing data — so it gets a sentence instead of a
 * blank, the way an unpriced consignment does on the gallery's own row.
 */
export function describeGallery(offer: TransferOffer): string {
  if (!offer.galleryOndoId || !offer.galleryName) return OFFERS_COPY.noGallery;
  return `${offer.galleryName} · ${offer.galleryOndoId}`;
}

/**
 * What the buyer would be taking on.
 *
 * THE LOCK IS THE HEADLINE, not the percentage. A share of resale profit is a
 * cost the buyer can price; a lock means the object cannot be sold on at all
 * until the term expires or the gallery lifts it, which is a fact about what
 * they are acquiring rather than about what it costs.
 *
 * An EXPIRED term reads as no covenant, because that is what it is — the
 * derivation is `covenantState`'s and the same one Sold Works uses. A buyer
 * being warned about an obligation that ended last year would be the screen
 * reading the row rather than the world.
 */
export function describeCovenant(offer: TransferOffer, now: Date): CovenantNotice {
  const endsAt = offer.covenantEndsAt;
  if (!endsAt || new Date(endsAt).getTime() <= now.getTime()) {
    return { kind: "none", summary: OFFERS_COPY.covenantNone };
  }

  const summary = OFFERS_COPY.covenantShare(
    formatBp(offer.covenantResaleShareBp),
    formatDate(endsAt),
  );

  return offer.covenantLocksTransfer
    ? { kind: "locked", summary, lock: OFFERS_COPY.covenantLocked }
    : { kind: "open", summary, lock: OFFERS_COPY.covenantOpen };
}

/**
 * What came with the picture, and whether accepting is gated on reading it.
 *
 * OND-85. THE DISTINCTION THIS FUNCTION EXISTS TO HOLD: `describeCovenant()`
 * above answers "what am I being asked to grant", and this answers "what am I
 * taking on". They are different rows in different sales, they were the same
 * three columns until OND-85, and a gallery buying a covenanted work read
 * "None — the work is yours to sell on freely" about a picture that owed money.
 *
 * AN EXPIRED TERM IS NOT FILTERED HERE, unlike the covenant above, and the
 * difference is deliberate: `ondo.binding_covenants()` already tests
 * `ends_at > now()`, so a row that arrives with a count has a live obligation
 * on it. Re-deriving the expiry from `inheritedCovenantEndsAt` would be the
 * screen second-guessing the predicate the SETTLEMENT is written from — and
 * when they disagreed, the screen would be the one that was wrong.
 *
 * `mustAcknowledge` IS NOT A CHOICE THIS FUNCTION MAKES. Whenever the count is
 * above zero the server refuses an acceptance without the acknowledgement, so
 * the flag is that fact rather than a policy of the view's.
 */
export function describeInherited(
  offer: TransferOffer,
  workBase: string,
  _now: Date,
): InheritedNotice {
  if (offer.inheritedCovenantCount === 0) {
    return { kind: "none", summary: OFFERS_COPY.inheritedNone };
  }

  return {
    // The LOCK is the headline when there is one, for `describeCovenant()`'s
    // reason: a share of profit is a cost a dealer can price into a bid, and a
    // lock means the stock cannot be turned over at all.
    kind: offer.inheritedLocksTransfer ? "locked" : "owing",
    summary: OFFERS_COPY.inheritedCount(offer.inheritedCovenantCount),
    // The date is `max(ends_at)` over rows that exist, so a count above zero
    // always has one. The fallback is there because a `string | null` has to be
    // narrowed and not because the branch is reachable — and it says "the term
    // ends" rather than inventing a date, which is the only honest thing to
    // print if the impossible happens.
    cost: OFFERS_COPY.inheritedShare(
      formatBp(offer.inheritedShareBpTotal),
      offer.inheritedCovenantEndsAt
        ? formatDate(offer.inheritedCovenantEndsAt)
        : OFFERS_COPY.inheritedUndated,
    ),
    lock: offer.inheritedLocksTransfer
      ? OFFERS_COPY.inheritedLocked
      : OFFERS_COPY.inheritedOpen,
    basis: OFFERS_COPY.inheritedBasis,
    detail: OFFERS_COPY.inheritedDetail,
    detailHref: `${workBase}/${offer.workId}`,
    mustAcknowledge: true,
  };
}

/**
 * Basis points as a percentage, without inventing precision.
 *
 * 5000 → '50', 250 → '2.5'. Trailing zeroes are dropped because '50.00%' reads
 * as a measurement and the number is a term somebody agreed in whole percents.
 */
export function formatBp(bp: number | null): string {
  if (bp === null) return "0";
  return String(Number((bp / 100).toFixed(2)));
}

/** '04 Feb 2031'. See `formatDay`; kept under this name for its test. */
export function formatDate(iso: string, locale = "en-GB"): string {
  return formatDay(iso, { locale });
}

/**
 * What went wrong, in words a collector can act on.
 *
 * A 404 or a 422 here is almost always the same thing in practice — the row
 * moved while the screen was open. `ondo.answer_transfer()` requires
 * `status = 'offered'`, so a withdrawal or a second tab answering first turns
 * the offer into something that no longer exists to answer.
 */
export function describeOffersError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 404 || cause.status === 403) return OFFERS_COPY.errors.gone;
    if (cause.message) return cause.message;

    return OFFERS_COPY.errors.refused;
  }

  return cause instanceof Error ? cause.message : OFFERS_COPY.errors.refused;
}
